import React, { useCallback, useEffect, useState } from "react";

import { setCurrentReceivedUrl } from "@/common/currentPageUrl";
import { decodeLineState } from "@/common/utils";
import { AgentSchema as AgentSchemaV1 } from "@/v1/types/client";
import { ParentSiteStateType } from "@/v1/utils/atoms";
import { sendFrameState } from "@/v2/hooks/useFrameControl";
import { getAgent } from "@/v2/libs/apiClient";
import { AgentSchema } from "@/v2/types/client";
import Loading from "@/v2/utils/Loading";
import {
  AGENT_VERSION,
  LINE_STATE_KEY,
  MESSAGE_TYPE,
  PARENT_ORIGIN_KEY,
  SOURCE_IFRAME,
  SOURCE_PARENT,
} from "@/v2/utils/constants";

// Keep PARENT_ORIGIN_KEY fresh so mobile LINE login always has a URL to return to

// Lazy load version-specific AppInitializers with CSS
// Use CSS entry files that import CSS before exporting component
// eslint-disable-next-line import/extensions
const AppInitializerStudio = React.lazy(() => import("@/v1/css-entry"));
// eslint-disable-next-line import/extensions
const AppInitializerNonStudio = React.lazy(() => import("@/v2/css-entry"));

/** Delay in ms to allow lazy-loaded CSS to be applied before rendering. */
const CSS_LOAD_DELAY_MS = 100;

/**
 * Root AppInitializer that routes to v1 or v2 based on agent version
 * - Receives messages from pitaliy.js
 * - Fetches agent data and determines version
 * - Renders appropriate version component
 */
function AppInitializer() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [version, setVersion] = useState<AGENT_VERSION | null>(null);
  const [cssLoaded, setCssLoaded] = useState<boolean>(false);
  const [agentId, setAgentId] = useState<string>("");
  const [agent, setAgent] = useState<AgentSchema | AgentSchemaV1 | null>(null);
  const [receivedUrl, setReceivedUrl] = useState<string>("");
  const [parentSiteState, setParentSiteState] = useState<ParentSiteStateType>(
    {},
  );
  const [parentWidth, setParentWidth] = useState<number>(0);
  const [lineOAuthCallback, setLineOAuthCallback] = useState<{
    code: string;
    state: string;
  } | null>(null);

  // Handle messages from pitaliy.js
  const handleMessage = useCallback((event: MessageEvent) => {
    if (typeof event.data !== "object") return;

    switch (event.data.type) {
      case MESSAGE_TYPE.HANDSHAKE_REQUEST:
        if (event.data.source === SOURCE_PARENT) {
          // Send handshake response to confirm iframe is ready
          sendFrameState({
            type: MESSAGE_TYPE.HANDSHAKE_RESPONSE,
            timestamp: Date.now(),
          });
        }
        break;
      case MESSAGE_TYPE.SET_URL:
        if (event.data.url) {
          setReceivedUrl(event.data.url);
          // Persist the parent page URL so mobile LINE login can redirect back
          // after LINE auth completes on b.com standalone (no postMessage there).
          localStorage.setItem(PARENT_ORIGIN_KEY, event.data.url);
          setCurrentReceivedUrl(event.data.url);
        }
        break;
      case MESSAGE_TYPE.AGENT_ID:
        if (event.data.agentId) {
          setAgentId(event.data.agentId);
        }
        if (event.data.parentWidth) {
          setParentWidth(event.data.parentWidth);
        }
        break;
      case MESSAGE_TYPE.LINE_OAUTH_CALLBACK:
        if (
          event.data.source === SOURCE_PARENT &&
          event.data.code &&
          event.data.state
        ) {
          setLineOAuthCallback({
            code: event.data.code,
            state: event.data.state,
          });
        }
        break;
      default:
        if (event.data.source === SOURCE_PARENT && event.data.state) {
          setParentSiteState(event.data.state);
        }
        break;
    }
  }, []);

  // Fetch agent and determine version, then load CSS
  const fetchAgentAndVersion = useCallback(async () => {
    if (!agentId) {
      // Keep loading state until agentId is received
      return;
    }

    try {
      const agentData = await getAgent({ agent_id: agentId });
      // Determine version from agent data, default to Studio if not specified
      const agentVersion = agentData.version || AGENT_VERSION.Studio;
      setVersion(agentVersion);
      setAgent(agentData );

      // CSS will be loaded by React.lazy; short delay so it is applied before render
      await new Promise<void>((resolve) => {
        setTimeout(resolve, CSS_LOAD_DELAY_MS);
      });
      setCssLoaded(true);

      // Send version to parent
      window.parent.postMessage(
        {
          source: SOURCE_IFRAME,
          type: MESSAGE_TYPE.AGENT_VERSION,
          version:
            agentVersion === AGENT_VERSION.Studio
              ? AGENT_VERSION.Studio
              : AGENT_VERSION.NonStudio,
        },
        "*",
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("AppInitializer: Failed to fetch agent data:", error);
      // Default to Studio on error
      setVersion(AGENT_VERSION.Studio);
      await new Promise<void>((resolve) => {
        setTimeout(resolve, CSS_LOAD_DELAY_MS);
      });
      setCssLoaded(true); // Allow rendering even on error
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  // Listen for messages from pitaliy.js
  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  // On mobile, LINE redirects back to b.com standalone. Parent URL and state
  // are encoded in the OAuth state param so we don't rely on localStorage
  // (which can be partitioned when b.com was loaded in an iframe).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const returnedState = params.get("state");

    if (!code || !returnedState) return;

    const payload = decodeLineState(returnedState);
    if (payload && payload.p) {
      const parentUrl = new URL(payload.p);
      parentUrl.searchParams.set("code", code);
      parentUrl.searchParams.set("state", returnedState);
      window.location.href = parentUrl.toString();
      return;
    }

    const savedState = localStorage.getItem(LINE_STATE_KEY);
    if (returnedState === savedState) {
      const pUrl = localStorage.getItem(PARENT_ORIGIN_KEY);
      if (pUrl) {
        const parentUrl = new URL(pUrl);
        parentUrl.searchParams.set("code", code);
        parentUrl.searchParams.set("state", returnedState);
        window.location.href = parentUrl.toString();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch agent when agentId is available
  useEffect(() => {
    fetchAgentAndVersion();
  }, [fetchAgentAndVersion]);

  // Show loading if any required data is missing
  if (!agentId || isLoading) {
    return <Loading className="w-screen h-screen" />;
  }

  // Show loading if version/agent not loaded yet or CSS not ready
  if (!version || !agent || !cssLoaded) {
    return <Loading className="w-screen h-screen" />;
  }

  // Route to appropriate version
  return (
    <React.Suspense fallback={<Loading className="w-screen h-screen" />}>
      {version === AGENT_VERSION.Studio ? (
        <AppInitializerStudio
          agentId={agentId}
          agent={agent as AgentSchemaV1}
          receivedUrl={receivedUrl}
          parentSiteState={parentSiteState}
          lineOAuthCallback={lineOAuthCallback}
          onLineOAuthCallbackHandled={() => setLineOAuthCallback(null)}
        />
      ) : (
        <AppInitializerNonStudio
          agentId={agentId}
          parentWidth={parentWidth}
          agent={agent as AgentSchema}
          receivedUrl={receivedUrl}
          lineOAuthCallback={lineOAuthCallback}
          onLineOAuthCallbackHandled={() => setLineOAuthCallback(null)}
        />
      )}
    </React.Suspense>
  );
}

export default AppInitializer;
